import type { Component } from 'solid-js';
import { Router, Route, Navigate } from '@solidjs/router';

import { Provider } from '../components/UserContextProvider';
import { ToastProvider } from '../components/MessageBox';
import { DialogHost } from '../components/Dialog';
import { SkullOwnerHost } from '../components/SkullOwnerView';
import { ItemInfoHost } from '../components/ItemInfoView';
import { CraftingResolverProvider } from '../services/craftingResolverContext';
import FeatureGate from '../components/FeatureGate';
import LicenseGate from '../components/LicenseGate';
import { GrantTypes } from '../services/grantsApi';
import Text from '../components/Text';
import AdminLayout from './AdminLayout';

import VerifyAccount from '../pages/VerifyAccount';
import { MSLoginPage } from '../pages/MSLogin';
import AccountSwitcher from '../pages/AccountSwitcher';

import Admin from '../pages/Admin';
import { UserPage } from '../pages/User';
import { AuditLogs } from '../pages/AuditLogs';
import TenantAdmin from '../pages/TenantAdmin';
import AdminNewTenant from '../pages/AdminNewTenant';
import AdminTenantDetails from '../pages/AdminTenantDetails';
import ProxyAdmin from '../pages/ProxyAdmin';
import BotRegistryAdmin from '../pages/BotRegistryAdmin';
import SchematicLicenses from '../pages/SchematicLicenses';
import FeatureFlagsAdmin from '../pages/FeatureFlagsAdmin';
import VektorPossessions from '../pages/VektorPossessions';
import AdminDiagnosticMonitors from '../pages/AdminDiagnosticMonitors';
import AdminPacketRecordings from '../pages/AdminPacketRecordings';
import AdminTaskCapsules from '../pages/AdminTaskCapsules';
import AdminCustomItems from '../pages/AdminCustomItems';
import AdminKoffermaticPerformance from '../pages/AdminKoffermaticPerformance';
import AdminCaseOpeningStats from '../pages/AdminCaseOpeningStats';
import AdminVoting from '../pages/AdminVoting';
import AdminVotingProxies from '../pages/AdminVotingProxies';
import AdminAlerts from '../pages/AdminAlerts';
import AdminPlayerBlacklist from '../pages/AdminPlayerBlacklist';
import AdminBotshopDomains from '../pages/AdminBotshopDomains';
import AdminCompressedPlacement from '../pages/AdminCompressedPlacement';
import AdminReconnectWindows from '../pages/AdminReconnectWindows';
import CitybuildStatusPage from '../pages/CitybuildStatus';
import AdminLicenses from '../pages/AdminLicenses';
import TenantGrants from '../pages/TenantGrants';
import BotEngineStats from '../pages/BotEngineStats';
import BotEngineHistory from '../pages/BotEngineHistory';
import { TranslateLog } from '../pages/TranslateLog';
import AppEventView from '../pages/AppEventView';
import AdminSettings from './AdminSettings';

import { AdminGate } from './AdminGate';

const FeatureDisabled = () => (
    <div style={{ padding: 'var(--spacing-2xl)', "max-width": '600px', margin: '0 auto', "text-align": 'center' }}>
        <h1><Text val="FEATURE_FLAGS_DISABLED_TITLE" /></h1>
        <p style={{ color: 'var(--dark-text-muted)' }}><Text val="FEATURE_FLAGS_DISABLED_BODY" /></p>
    </div>
);

const LicenseDenied = () => (
    <div style={{ padding: 'var(--spacing-2xl)', "max-width": '600px', margin: '0 auto', "text-align": 'center' }}>
        <h1><Text val="LICENSE_DENIED_TITLE" /></h1>
        <p style={{ color: 'var(--dark-text-muted)' }}><Text val="LICENSE_DENIED_BODY" /></p>
    </div>
);

const AdminGated = (flag: string, page: () => any) => (
    <FeatureGate flag={flag} fallback={<AdminGate><FeatureDisabled /></AdminGate>}>
        <AdminGate>{page()}</AdminGate>
    </FeatureGate>
);

const AdminGatedAndLicensed = (flag: string, license: string, page: () => any) => (
    <FeatureGate flag={flag} fallback={<AdminGate><FeatureDisabled /></AdminGate>}>
        <LicenseGate type={license} fallback={<AdminGate><LicenseDenied /></AdminGate>}>
            <AdminGate>{page()}</AdminGate>
        </LicenseGate>
    </FeatureGate>
);

const AdminApp: Component = () => {

    return (
        <div>
            <Provider>
                <ToastProvider>
                    <CraftingResolverProvider>
                    <Router>
                        <Route path="/verifyaccount" component={() => <AdminLayout><VerifyAccount /></AdminLayout>} />
                        <Route path="/oauth/ms/callback" component={() => <AdminLayout><MSLoginPage /></AdminLayout>} />
                        <Route path="/account-switcher" component={() => <AccountSwitcher />} />

                        <Route path="/" component={() => <AdminGate><Admin /></AdminGate>} />
                        <Route path="/admin" component={() => <AdminGate><Admin /></AdminGate>} />
                        <Route path="/user/:id" component={() => <AdminGate><UserPage /></AdminGate>} />
                        <Route path="/auditlogs" component={() => <AdminGate><AuditLogs /></AdminGate>} />
                        <Route path="/auditlogs/:id" component={() => <AdminGate><AuditLogs /></AdminGate>} />
                        <Route path="/admin/tenants" component={() => <AdminGate><TenantAdmin /></AdminGate>} />
                        <Route path="/admin/tenants/new" component={() => <AdminGate><AdminNewTenant /></AdminGate>} />
                        <Route path="/admin/tenants/:id" component={() => <AdminGate><AdminTenantDetails /></AdminGate>} />
                        <Route path="/admin/tenant/:id/grants" component={() => <AdminGate><TenantGrants /></AdminGate>} />
                        <Route path="/admin/proxies" component={() => AdminGated('PROXIES', () => <ProxyAdmin />)} />
                        <Route path="/admin/bots" component={() => <AdminGate><BotRegistryAdmin /></AdminGate>} />
                        <Route path="/admin/schematic-licenses" component={() => <AdminGate><SchematicLicenses /></AdminGate>} />
                        <Route path="/admin/feature-flags" component={() => <AdminGate><FeatureFlagsAdmin /></AdminGate>} />
                        <Route path="/admin/compressed-placement" component={() => <AdminGate><AdminCompressedPlacement /></AdminGate>} />
                        <Route path="/admin/possessions" component={() => AdminGatedAndLicensed('VEKTOR_GOGGLES', GrantTypes.LicenseVektorGoggles, () => <VektorPossessions />)} />
                        <Route path="/admin/goggles-monitoring" component={() => <AdminGate><AdminDiagnosticMonitors /></AdminGate>} />
                        <Route path="/admin/packet-recordings" component={() => <AdminGate><AdminPacketRecordings /></AdminGate>} />
                        <Route path="/admin/task-capsules" component={() => <AdminGate><AdminTaskCapsules /></AdminGate>} />
                        <Route path="/admin/custom-items" component={() => <AdminGate><AdminCustomItems /></AdminGate>} />
                        <Route path="/admin/koffermatic-performance" component={() => <AdminGate><AdminKoffermaticPerformance /></AdminGate>} />
                        <Route path="/admin/case-stats" component={() => AdminGated('CASE_OPENING', () => <AdminCaseOpeningStats />)} />
                        <Route path="/admin/voting" component={() => <AdminGate><AdminVoting /></AdminGate>} />
                        <Route path="/admin/voting/proxies" component={() => <AdminGate><AdminVotingProxies /></AdminGate>} />
                        <Route path="/admin/alerts" component={() => <AdminGate><AdminAlerts /></AdminGate>} />
                        <Route path="/admin/blacklist" component={() => AdminGated('SELF_SHUTOFF', () => <AdminPlayerBlacklist />)} />
                        <Route path="/admin/botshop-domains" component={() => <AdminGate><AdminBotshopDomains /></AdminGate>} />
                        <Route path="/admin/reconnect-windows" component={() => <AdminGate><AdminReconnectWindows /></AdminGate>} />
                        <Route path="/admin/citybuild-status" component={() => AdminGated('CITYBUILD_STATUS', () => <CitybuildStatusPage />)} />
                        <Route path="/admin/licenses" component={() => <AdminGate><AdminLicenses /></AdminGate>} />
                        <Route path="/admin/settings" component={() => <AdminGate><AdminSettings /></AdminGate>} />
                        <Route path="/botengine/stats" component={() => <AdminGate><BotEngineStats /></AdminGate>} />
                        <Route path="/botengine/history" component={() => <AdminGate><BotEngineHistory /></AdminGate>} />
                        <Route path="/log" component={() => <AdminGate><TranslateLog /></AdminGate>} />
                        <Route path="/eventviewer" component={() => <AdminGate><AppEventView /></AdminGate>} />

                        {/* Shared code (sfetch.tsx, Navbar.tsx) hardcodes redirects to these
                            main-app-only paths (/login, /verifysession, /me) on session edge
                            cases. Routing them back through /admin re-runs AdminGate's own
                            check instead of leaving the router with nothing to render. */}
                        <Route path="/login" component={() => <Navigate href="/admin" />} />
                        <Route path="/verifysession" component={() => <Navigate href="/admin" />} />
                        <Route path="/me" component={() => <Navigate href="/admin" />} />
                        <Route path="*" component={() => <Navigate href="/admin" />} />
                    </Router>
                    </CraftingResolverProvider>
                </ToastProvider>
                <DialogHost />
                <SkullOwnerHost />
                <ItemInfoHost />
            </Provider>
        </div>
    );
};

export default AdminApp;
